import { Table } from "antd";
import type { TableProps } from "antd";

import useColumns from "./helpers/useColumns";
import useDataSource from "./helpers/useDataSource";
import useAttachmentsCount from "./helpers/useAttachmentsCount";
import InvestmentAttachmentsTable from "./InvestmentAttachmentsTable";
import type {
  SetState,
  TableRef,
  Investment,
  Transaction,
  InvestorFile,
  CustomTarget,
  CustomAmounts,
  TransferInvestment,
} from "./types";

type Props = {
  table: TableRef;
  sourceFiles: InvestorFile[];
  customAmounts: CustomAmounts[];
  customTargets: CustomTarget[];
  sourceProjectId?: string;
  targetProjectId?: string;
  setCustomTargets: SetState<CustomTarget[]>;
  sourceInvestments: Investment[];
  targetInvestments: Investment[];
  sourceTransactions: Transaction[];
  getSourceBatchName: (batchId: string) => string | undefined;
};

const InvestmentsTransferTable = ({
  table,
  sourceFiles,
  customAmounts,
  customTargets,
  sourceProjectId = undefined,
  targetProjectId = undefined,
  setCustomTargets,
  sourceInvestments,
  targetInvestments,
  sourceTransactions,
  getSourceBatchName,
}: Props) => {
  const getInvestmentAttachmentsCount = useAttachmentsCount({
    sourceFiles,
    sourceTransactions,
  });

  const columns = useColumns({
    targetProjectId,
    sourceProjectId,
    sourceInvestments,
    targetInvestments,
    getInvestmentAttachmentsCount,
    customAmounts,
    customTargets,
    setCustomTargets,
  });

  const dataSource = useDataSource({
    customTargets,
    sourceInvestments,
    targetInvestments,
  });

  table.current.getDataSource = () => dataSource;

  const expandedRowRender = (investment: TransferInvestment) => {
    const { id, isIncluded, sourceInvestmentId } = investment;

    let files: InvestorFile[] = [];
    let transactions: Transaction[] = [];
    let investmentId = id;
    let getBatchName = getSourceBatchName;

    if (isIncluded) {
      files = sourceFiles;
      transactions = sourceTransactions;
      investmentId = sourceInvestmentId || id;
      getBatchName = getSourceBatchName;
    }

    return (
      <InvestmentAttachmentsTable
        files={files}
        transactions={transactions}
        investmentId={investmentId}
        getBatchName={getBatchName}
      />
    );
  };

  const tableProps: TableProps<TransferInvestment> = {
    size: "middle",
    rowKey: "id",
    bordered: true,
    pagination: false,
    expandable: {
      rowExpandable: (investment) => !!getInvestmentAttachmentsCount(investment),
      expandedRowRender,
    },
    columns,
    dataSource,
  };

  return (
    <Table {...tableProps} />
  );
};

export default InvestmentsTransferTable;
